// src/components/dashboard/Workshop2Dashboard.js
// Professional Workshop 2 Dashboard using Tremor components (Light Theme)
import React from 'react';
import {
    Card,
    Title,
    Text,
    Metric,
    Grid,
    DonutChart,
    BarList,
    Flex,
    Legend,
    Badge,
    BadgeDelta,
    ProgressBar
} from "@tremor/react";
import { useTranslation } from 'react-i18next';
import {
    Target,
    Shield,
    AlertTriangle,
    Users,
    Zap,
    TrendingUp
} from 'lucide-react';

const Workshop2Dashboard = ({
    dreadedEvents = [],
    eventsBySeverityData = [],
    eventsByImpactData = [],
    w2Sources = [],
    w2SourcesByCategoryData = [],
    w2MappingsBySourceData = [],
    w2Loading = false
}) => {
    const { t } = useTranslation();

    // Calculate totals
    const totalEvents = dreadedEvents.length;
    const totalSources = w2Sources.length;
    const totalMappings = w2MappingsBySourceData.reduce((sum, item) => sum + item.value, 0);

    // Count by severity
    const criticalEvents = eventsBySeverityData.filter(s =>
        s.name === 'Critique' || s.name === 'Catastrophique' ||
        s.name === 'Critical' || s.name === 'Catastrophic'
    ).reduce((sum, s) => sum + s.value, 0);

    // Calculate high-risk sources
    const highRiskSources = w2MappingsBySourceData.filter(s => s.riskLevel >= 3).length;

    // Color arrays for charts
    const pillarColors = ["amber", "rose", "violet", "emerald", "blue", "cyan", "fuchsia"];
    const severityColors = ["slate", "rose", "orange", "amber", "emerald", "gray"];
    const categoryColors = ["rose", "violet", "orange", "emerald", "blue", "cyan", "amber"];

    // Process category data for chart
    const categoryChartData = w2SourcesByCategoryData.map(item => ({
        name: item.name,
        value: item.value
    }));

    // Process sources by risk for bar list (no icon property - BarList doesn't support it)
    const sourcesByRiskData = w2MappingsBySourceData.slice(0, 8).map(item => ({
        name: item.name,
        value: item.value
    }));

    if (w2Loading) {
        return (
            <Card className="!bg-white">
                <Flex justifyContent="center" alignItems="center" className="h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-600 border-t-transparent mr-3"></div>
                    <Text>{t('dashboard.status.loading')}</Text>
                </Flex>
            </Card>
        );
    }

    return (
        <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
            {/* KPI Cards Row */}
            <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
                {/* Dreaded Events Card */}
                <Card decoration="top" decorationColor="orange" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop2.dreadedEvents')}</Text>
                            <Metric className="!text-gray-900">{totalEvents}</Metric>
                            {criticalEvents > 0 && (
                                <BadgeDelta deltaType="increase" size="xs" className="mt-1">
                                    {criticalEvents} {t('dashboard.cards.critical')}
                                </BadgeDelta>
                            )}
                        </div>
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-orange-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Risk Sources Card */}
                <Card decoration="top" decorationColor="red" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop2.riskSources')}</Text>
                            <Metric className="!text-gray-900">{totalSources}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {w2SourcesByCategoryData.length} {t('dashboard.workshop2.categories')}
                            </Text>
                        </div>
                        <div className="p-2 bg-red-100 rounded-lg">
                            <Target className="w-6 h-6 text-red-600" />
                        </div>
                    </Flex>
                </Card>

                {/* High Risk Sources Card */}
                <Card decoration="top" decorationColor="rose" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop2.highRiskSources')}</Text>
                            <Metric className="!text-gray-900">{highRiskSources}</Metric>
                            {highRiskSources > 0 && (
                                <Badge color="rose" size="xs" className="mt-1">
                                    {t('dashboard.workshop2.requiresAttention')}
                                </Badge>
                            )}
                        </div>
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <Zap className="w-6 h-6 text-rose-600" />
                        </div>
                    </Flex>
                </Card>

                {/* SR/OV Mappings Card */}
                <Card decoration="top" decorationColor="violet" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop2.mappings')}</Text>
                            <Metric className="!text-gray-900">{totalMappings}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {t('dashboard.workshop2.sourceTargetPairs')}
                            </Text>
                        </div>
                        <div className="p-2 bg-violet-100 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-violet-600" />
                        </div>
                    </Flex>
                </Card>
            </Grid>

            {/* Charts Grid */}
            <Grid numItems={1} numItemsLg={2} className="gap-6">
                {/* Threat Categories - Donut Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop2.threatCategories')}</Title>
                    {categoryChartData.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={categoryChartData}
                                category="value"
                                index="name"
                                colors={categoryColors}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={categoryChartData.map(d => d.name)}
                                colors={categoryColors}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop2.noSources')}</Text>
                        </div>
                    )}
                </Card>

                {/* Top Risk Sources - Bar List */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop2.topRiskSources')}</Title>
                    {sourcesByRiskData.length > 0 ? (
                        <BarList
                            data={sourcesByRiskData}
                            className="mt-4"
                            showAnimation={true}
                            color="orange"
                        />
                    ) : (
                        <div className="h-40 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop2.noSources')}</Text>
                        </div>
                    )}
                </Card>
            </Grid>

            {/* Risk Level Summary */}
            {w2MappingsBySourceData.length > 0 && (
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop2.riskLevelSummary')}</Title>
                    <Grid numItems={1} numItemsMd={3} className="gap-4 mt-4">
                        {(() => {
                            const high = w2MappingsBySourceData.filter(s => s.riskLevel >= 3).length;
                            const medium = w2MappingsBySourceData.filter(s => s.riskLevel === 2).length;
                            const low = w2MappingsBySourceData.filter(s => s.riskLevel <= 1).length;
                            const total = w2MappingsBySourceData.length;

                            return (
                                <>
                                    <Card decoration="left" decorationColor="rose" className="!bg-white">
                                        <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                            <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                                            <Text className="!text-gray-700">{t('dashboard.workshop2.highRisk')}</Text>
                                        </Flex>
                                        <Metric className="mt-2 !text-gray-900">{high}</Metric>
                                        <ProgressBar
                                            value={total > 0 ? (high / total) * 100 : 0}
                                            color="rose"
                                            className="mt-2"
                                        />
                                    </Card>
                                    <Card decoration="left" decorationColor="amber" className="!bg-white">
                                        <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                            <Text className="!text-gray-700">{t('dashboard.workshop2.mediumRisk')}</Text>
                                        </Flex>
                                        <Metric className="mt-2 !text-gray-900">{medium}</Metric>
                                        <ProgressBar
                                            value={total > 0 ? (medium / total) * 100 : 0}
                                            color="amber"
                                            className="mt-2"
                                        />
                                    </Card>
                                    <Card decoration="left" decorationColor="emerald" className="!bg-white">
                                        <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                            <Text className="!text-gray-700">{t('dashboard.workshop2.lowRisk')}</Text>
                                        </Flex>
                                        <Metric className="mt-2 !text-gray-900">{low}</Metric>
                                        <ProgressBar
                                            value={total > 0 ? (low / total) * 100 : 0}
                                            color="emerald"
                                            className="mt-2"
                                        />
                                    </Card>
                                </>
                            );
                        })()}
                    </Grid>
                </Card>
            )}
        </div>
    );
};

export default Workshop2Dashboard;
